import React from 'react';
import { CalendarIcon, ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import DatePickerField from './DatePickerField';
import { EMPTY_DATE_FILTER, describeDateFilter, isDateFilterActive } from '../utils/dateFilter';

const MAX_LAST_DAYS = 90;

/**
 * "Date" button that opens a from/to range or "last N days" filter.
 * Picking a range clears last N days, and last N days is disabled while a range is set,
 * so only one applies.
 * @param {{from, to, lastDays}} value - Current filter (see utils/dateFilter)
 * @param {(value) => void} onChange - Called with the new filter
 */
const DateFilter = ({ value, onChange }) => {
    const isActive = isDateFilterActive(value);
    const hasRange = Boolean(value.from || value.to);

    const setRange = (range) => onChange({ ...value, ...range, lastDays: null });

    const handleLastDaysChange = (e) => {
        const days = parseInt(e.target.value, 10);
        // Anything above the max is capped rather than rejected
        onChange({ ...EMPTY_DATE_FILTER, lastDays: days > 0 ? Math.min(days, MAX_LAST_DAYS) : null });
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                        "h-8 gap-2 px-3 text-[13px] font-semibold",
                        isActive && "border-primary/50 text-primary bg-primary/10 hover:bg-primary/15"
                    )}
                >
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {describeDateFilter(value)}
                    <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-3 space-y-3" align="start">
                <div className="space-y-1.5">
                    <p className="text-xs font-medium">Date range</p>
                    <div className="flex items-center gap-2">
                        <DatePickerField
                            label="From"
                            className="flex-1 min-w-0"
                            value={value.from}
                            maxDate={value.to}
                            onChange={(from) => setRange({ from })}
                        />
                        <DatePickerField
                            label="To"
                            className="flex-1 min-w-0"
                            value={value.to}
                            minDate={value.from}
                            onChange={(to) => setRange({ to })}
                        />
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                        No From = from the start, no To = until now
                    </p>
                </div>

                <div className="flex items-center gap-2 text-[10px] uppercase text-muted-foreground">
                    <div className="h-px flex-1 bg-border" />
                    or
                    <div className="h-px flex-1 bg-border" />
                </div>

                <div className="space-y-1.5">
                    <p className={cn("text-xs font-medium", hasRange && "text-muted-foreground")}>
                        Last
                        {hasRange && <span className="font-normal"> · clear From/To to use</span>}
                    </p>
                    <div className="flex items-center gap-2">
                        <Input
                            type="number"
                            min={1}
                            max={MAX_LAST_DAYS}
                            placeholder={`Number of days (1–${MAX_LAST_DAYS})`}
                            className="h-8 text-xs"
                            value={value.lastDays ?? ''}
                            onChange={handleLastDaysChange}
                            disabled={hasRange}
                        />
                        <span className="text-xs text-muted-foreground shrink-0">days</span>
                    </div>
                </div>

                {isActive && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full h-7 gap-1 text-xs text-muted-foreground"
                        onClick={() => onChange(EMPTY_DATE_FILTER)}
                    >
                        <X className="h-3 w-3" />
                        Clear date filter
                    </Button>
                )}
            </PopoverContent>
        </Popover>
    );
};

export default DateFilter;
